import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import * as styles from "./CloneAccess.module.scss";
import { Dropdown, DropdownType } from "cprime-foundational-components";
import Button from "../../../Components/Common/CprimeButton";
import {
  cloneRequestApi,
  getEmailSearchResults,
} from "../../../services/RequestAccess/RequestAccess";
import { triggerToast } from "../../../utils/toast";
import { updateCurrentStep } from "../../../store/slices/featureGroupWithFeatureSlice";
import { AppDispatch, RootState } from "../../../store/store";
import { useDispatch, useSelector } from "react-redux";
import { setClonedFeatureIds } from "../../../store/slices/featurePermissionSlice";

interface CloneAccessProps {
  open: boolean;
  onClose: () => void;
}
function CloneAccessDialog({ open, onClose }: CloneAccessProps) {
  const { selectedUsers } = useSelector(
    (state: RootState) => state?.applicationDomain ?? [],
  );
  const [userLists, setUserList] = useState<any>([]);
  const [userOptionInput, setUserOptionInput] = useState("");
  const [selectedUser, setSelectedUser] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(
    null,
  );
  const scssClass = styles as any;
  const dispatch = useDispatch<AppDispatch>();

  const cloneAccessRequest = async () => {
    setLoading(true);
    const userData = selectedUsers?.map((item: any) => ({
      displayName: item?.displayName,
      mail: item?.mail,
      user_workday_id: item?.user_workday_id,
    }));

    const response = (await cloneRequestApi(
      selectedUser[0]?.id,
      userData,
    )) as any;
    if (response.status !== "SUCCESS") {
      triggerToast(
        response?.message || "Error while fetching permission",
        "error",
      );
      setLoading(false);
      return;
    } else {
      triggerToast(
        response?.message || "Permission fetched successfully",
        "success",
      );
      const featureIds = response?.data;
      // ?.filter((row: any) => !row?.disabled)
      // ?.map((row: any) => row?.feature_id);
      dispatch(setClonedFeatureIds(featureIds));
      dispatch(updateCurrentStep(3));
      setLoading(false);
      onClose();
    }
  };

  const searchUser = async (query: string) => {
    try {
      if (query?.length >= 1) {
        let name = "";
        let email = "";
        if (query.includes("@") || query.includes(".")) {
          email = query.trim();
        } else {
          name = query.trim();
        }

        let param: "name" | "email";
        let value: string;
        if (email) {
          param = "email";
          value = email;
        } else {
          param = "name";
          value = name;
        }

        const response = await getEmailSearchResults(
          encodeURIComponent(value),
          param,
        );
        const results =
          response?.data?.map((user: any) => ({
            id: user?.mail,
            label: param === "email" ? user?.mail : user?.displayName,
            user_workday_id: user?.user_workday_id || "",
          })) || [];
        setUserList(results);
      } else {
        setUserList([]);
      }
    } catch (err) {
      setUserList([]);
    }
  };

  return (
    <Dialog
      open={open}
      // onClose={() => onClose()}
      fullWidth
      maxWidth={`sm`}
      sx={{
        "& .MuiDialogContent-root": {
          paddingBottom: "0.5rem !important",
          paddingTop: "0px !important",
          paddingLeft: "1.5rem !important",
          paddingRight: "1.5rem !important",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 400,
          fontSize: "1.5rem",
          lineHeight: "2rem",
          color: "#000000",
        }}
      >
        Clone Access
        <IconButton onClick={onClose} data-testid="closeModal">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <div style={{ maxHeight: "calc(100vh - 300px)", width: "100%" }}>
          <div style={{ fontSize: "14px" }}>
            Select user whose access you want to clone and request.
          </div>
          <Dropdown
            selectedOptions={selectedUser || []}
            aria-label="select_user"
            className={scssClass.dropdown}
            width={"100%"}
            options={userLists}
            placeholder="Search and select user"
            label="Users"
            optionsTextLimit={200}
            type={DropdownType.FilterSingleSelect}
            inputValue={userOptionInput}
            onInputChange={(_event, value) => {
              const searchValue = value?.split("|")[0];
              setUserOptionInput(searchValue);
              if (debounceTimer) clearTimeout(debounceTimer);
              const timer = setTimeout(() => {
                searchUser(searchValue);
              }, 300);
              setDebounceTimer(timer);
            }}
            onChange={(options) => {
              const selected = options[0] as any;
              setSelectedUser(options);
              setUserOptionInput(selected?.label);
            }}
            onClose={() => {}}
            onOpen={() => {
              if (!userOptionInput) searchUser("");
            }}
          />
          <div className={scssClass.divider} />
          <div className={scssClass.responsiveCard}>
            <div className={scssClass.cardContent}>
              <div className={scssClass.buttonSection}>
                <Button
                  variant="tertiary"
                  sx={{ marginLeft: "4px", marginRight: "4px" }}
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  disabled={selectedUser?.length === 0}
                  sx={{ marginLeft: "4px", marginRight: "4px" }}
                  onClick={cloneAccessRequest}
                  loading={loading}
                >
                  Submit
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default CloneAccessDialog;
